import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "@/context/StoreContext";
import { errMsg, inr } from "@/lib/api";

export default function PincodeCheck() {
  const { pincodeInfo, checkPincode } = useStore();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    if (!/^\d{6}$/.test(value)) { toast.error("Enter a valid 6-digit pincode"); return; }
    setBusy(true);
    try {
      const info = await checkPincode(value);
      if (info.serviceable) toast.success(`Delivering via ${info.store_name}`);
      else toast.error(info.message || "Not serviceable yet");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="relative">
      <button
        data-testid="pincode-button"
        onClick={() => setOpen((o) => !o)}
        className="hidden h-10 items-center gap-2 rounded-full border border-champagne/40 px-4 font-mono text-[10px] uppercase tracking-[0.15em] text-cream transition-colors hover:border-gold hover:text-gold md:flex"
      >
        <MapPin className="h-3.5 w-3.5 text-gold" />
        {pincodeInfo?.pincode ? `${pincodeInfo.pincode}` : "Deliver to"}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="pincode-popover"
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            className="absolute right-0 top-12 z-[75] w-72 rounded-2xl border border-champagne/50 bg-pine p-4 shadow-2xl shadow-gold/15"
          >
            <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold">Delivery Pincode</p>
            <div className="mt-3 flex gap-2">
              <input
                data-testid="pincode-input"
                type="tel" inputMode="numeric" maxLength={6}
                value={value}
                onChange={(e) => setValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
                onKeyDown={(e) => e.key === "Enter" && apply()}
                placeholder="e.g. 600001"
                className="w-full rounded-xl border border-champagne/50 bg-forest px-3 py-2.5 font-mono text-sm text-cream placeholder:text-fern focus:border-gold focus:outline-none"
              />
              <button
                data-testid="pincode-apply-btn"
                onClick={apply} disabled={busy}
                className="rounded-xl bg-gold px-4 font-mono text-[10px] font-bold uppercase tracking-widest text-forest disabled:opacity-60"
              >
                {busy ? "…" : "Check"}
              </button>
            </div>
            {pincodeInfo && (
              <div data-testid="pincode-status" className={`mt-3 flex items-start gap-2 rounded-xl px-3 py-2.5 text-xs ${pincodeInfo.serviceable ? "bg-moss text-cream" : "bg-gold/10 text-gold"}`}>
                {pincodeInfo.serviceable ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gold" /> : <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
                <span>
                  {pincodeInfo.serviceable
                    ? <>Serving {pincodeInfo.state} via <b>{pincodeInfo.store_name}</b></>
                    : pincodeInfo.message || "We don't deliver here yet"}
                </span>
              </div>
            )}
            {pincodeInfo?.serviceable && (pincodeInfo.shipping_charge ?? pincodeInfo.delivery_charge) != null && (
              <p data-testid="pincode-shipping" className="mt-2 rounded-xl bg-moss px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-gold">
                {(pincodeInfo.shipping_charge ?? pincodeInfo.delivery_charge) > 0
                  ? `Shipping to ${pincodeInfo.pincode}: ${inr(pincodeInfo.shipping_charge ?? pincodeInfo.delivery_charge)}`
                  : `Free shipping to ${pincodeInfo.pincode}`}
              </p>
            )}
            {pincodeInfo?.serviceable && (pincodeInfo.free_delivery_above ?? pincodeInfo.free_shipping_above) > 0 && (
              <p data-testid="pincode-free-above" className="mt-2 rounded-xl bg-moss px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-sage">
                Free delivery over {inr(pincodeInfo.free_delivery_above ?? pincodeInfo.free_shipping_above)}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
